
import React from 'react';
import { Church } from './types';
import { useLanguage } from '../../../contexts/language';
import { Church as ChurchIcon, MapPin, Users } from 'lucide-react';

interface ChurchesListProps {
  churches: Church[];
  loading: boolean;
  selectedChurchId: string | undefined;
  onChurchSelect: (church: Church) => void;
}

const ChurchesList: React.FC<ChurchesListProps> = ({ 
  churches, 
  loading, 
  selectedChurchId, 
  onChurchSelect 
}) => {
  const { t } = useLanguage();
  
  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-2 text-sm text-gray-500">{t('loadingChurches')}</p>
      </div>
    );
  }
  
  if (churches.length === 0) {
    return (
      <div className="text-center py-6">
        <ChurchIcon className="h-10 w-10 text-gray-400 mx-auto" />
        <p className="mt-2 text-gray-500">{t('noChurchesFound')}</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3 max-h-80 overflow-y-auto">
      {churches.map(church => (
        <div 
          key={church.id} 
          className={`bg-gray-50 p-3 rounded-lg shadow-sm cursor-pointer transition-colors hover:bg-gray-100 ${
            selectedChurchId === church.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
          }`}
          onClick={() => onChurchSelect(church)}
        >
          <div className="flex items-start">
            <ChurchIcon className={`h-5 w-5 mr-2 mt-0.5 ${selectedChurchId === church.id ? 'text-blue-600' : 'text-blue-500'}`} />
            <div>
              <h3 className="font-semibold">{church.name}</h3>
              <div className="text-sm text-gray-600">{church.denomination}</div>
              <div className="flex items-center mt-2 text-sm text-gray-500">
                <MapPin className="h-4 w-4 mr-1" />
                <span>{church.location}</span>
              </div>
              <div className="flex items-center mt-1 text-sm text-gray-500">
                <Users className="h-4 w-4 mr-1" />
                <span>{t('missionaries')}: {church.missionaries}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ChurchesList;
